import React, { useEffect, useState, useRef } from 'react';
import API_BASE_URL from '../config';
import "../Styles/certified.css";

const ORG_TYPES = [
  { key: 'micro', title: 'Micro Organizations', subtitle: '(10 - 99 Employees)' },
  { key: 'mid', title: 'Mid-Scale Organizations', subtitle: '(100 - 499 Employees)' },
  { key: 'large', title: 'Large Organizations', subtitle: '(500 - 2999 Employees)' },
  { key: 'enterprise', title: 'Enterprise Organizations', subtitle: '(3000+ Employees)' },
];

export default function CertifiedOrganizations() {
  const [selectedType, setSelectedType] = useState('all');
  const [orgs, setOrgs] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(25);
  const tableRef = useRef(null);
  const scrollToTable = () => {
    tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    document.title = 'Certified Organizations | Outstanding Workplaces!';
    fetchOrgs();
    // eslint-disable-next-line
  }, [selectedType, page, search, limit]);

  const fetchOrgs = async () => {
    let allOrgs = [];
    let allTotal = 0;

    if (selectedType === 'all') {
      for (const type of ORG_TYPES) {
        const res = await fetch(
          `${API_BASE_URL}/api/certified-organizations/${type.key}?page=1&limit=10000&search=${encodeURIComponent(search)}`
        );
        const data = await res.json();
        if (data.organizations) {
          allOrgs = allOrgs.concat(data.organizations.map(org => ({ ...org, orgType: type.title })));
          allTotal += data.organizations.length;
        }
      }
      setTotal(allOrgs.length);
      const pagedOrgs = allOrgs.slice((page - 1) * limit, page * limit);
      setOrgs(pagedOrgs);
    } else {
      const res = await fetch(
        `${API_BASE_URL}/api/certified-organizations/${selectedType}?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`
      );
      const data = await res.json();
      setOrgs(
        (data.organizations || []).map(org => ({
          ...org,
          orgType: ORG_TYPES.find(t => t.key === selectedType)?.title || ''
        }))
      );
      setTotal(data.total || 0);
    }
  };

  const handleCardClick = (type) => {
    setSelectedType(type);
    setPage(1);
    setSearch('');
  };

  return (
    <div className="micro-bg">
      {/* Hero Section */}
      <section className="micro-hero-section">
        <div className="micro-hero-row">
          <div className="micro-hero-left">
            <img
              src="/assets/Badge_TM.png"
              alt="Outstanding Workplaces Certified 2025"
              className="micro-certified-badge"
            />
            <h1 className="micro-hero-title">
              Meet Our Certified Top Outstanding Workplaces!
            </h1>
            <p className="micro-hero-desc">
              We're proud to announce the top workplaces in the country that have been certified by Outstanding Workplaces through a thorough assessment of their work culture. These organizations have demonstrated a commitment to creating a positive and productive work environment that benefits their employees and drives business success.
            </p>
          </div>
          <div className="micro-hero-right">
            <img
              src="/cfg.png"
              alt="Certified Person"
              className="micro-hero-img"
            />
          </div>
        </div>
      </section>

      <section className="micro-blue-section">
        <div className="micro-blue-container">
          <h2 className="micro-blue-title">
            Check out our list of Certified top workplaces and get inspired!
          </h2>
        </div>
      </section>

      <section className="micro-cards-section">
        <div className="micro-cards-row">
          {ORG_TYPES.map(type => (
            <OrgCard
              key={type.key}
              title={type.title}
              subtitle={type.subtitle}
              active={selectedType === type.key}
              onClick={() => handleCardClick(type.key)}
            />
          ))}
        </div>
      </section>

      <section className="micro-section-title">
        <div className="micro-section-title-container">
          <h2 className="micro-section-title-text">
            {selectedType === 'all'
              ? 'Certified Organizations'
              : ORG_TYPES.find(t => t.key === selectedType)?.title}
          </h2>
        </div>
      </section>

   <section className="micro-table-section" ref={tableRef}>
        <div className="micro-table-container">
          <div className="micro-table-header">
            <div className="micro-table-entities">
              Show:&nbsp;
              <select
                value={limit}
                onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
                className="micro-table-select"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              &nbsp;entries
            </div>
            <div className="micro-table-searchbox">
              Search:&nbsp;
              <input
                type="text"
                placeholder="Search organizations..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                className="micro-table-search"
              />
            </div>
          </div>
          <div className="micro-table-wrapper">
            <table className="micro-table">
              <thead>
                <tr>
                  <th className="micro-th">Organization Name</th>
                  <th className="micro-th">Industry</th>
                  <th className="micro-th">No. of Employees</th>
                  <th className="micro-th">Certification Period</th>
                  <th className="micro-th">Company Profile</th>
                  {/* {selectedType === 'all' && (
                    <th className="micro-th">Organization Type</th>
                  )} */}
                </tr>
              </thead>
              <tbody>
                {orgs.length === 0 ? (
                  <tr>
                    <td colSpan={selectedType === 'all' ? 6 : 5} className="micro-td-center">No organizations found.</td>
                  </tr>
                ) : orgs.map((org, idx) => (
                  <tr key={idx}>
                    <td className="micro-td">{org.organization_name}</td>
                    <td className="micro-td">{org.industry}</td>
                    <td className="micro-td">{org.number_of_employees}</td>
                    <td className="micro-td">{org.certification_period}</td>
                    <td className="micro-td">
                      {org.profile_url && org.status ? (
                        <a href={org.profile_url} target="_blank" rel="noopener noreferrer" className="micro-profile-btn">
                          View
                        </a>
                      ) : org.profile_url ? (
                        <button className="micro-profile-btn micro-profile-btn-disabled" disabled>
                          View
                        </button>
                      ) : "-"}
                    </td>
                    {/* {selectedType === 'all' && (
                      <td className="micro-td">{org.orgType}</td>
                    )} */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="micro-table-footer">
            <div className="micro-table-info">
              {total > 0
                ? `Showing ${((page - 1) * limit) + 1} to ${Math.min(page * limit, total)} of ${total} entries`
                : 'Showing 0 to 0 of 0 entries'}
            </div>
            <div className="micro-pagination">
              <button
                disabled={page === 1}
                onClick={() => { setPage(page - 1); scrollToTable(); }}
                className="micro-page-btn"
              >
                Previous
              </button>
              {Array.from({ length: Math.min(5, Math.ceil(total / limit)) }, (_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                   onClick={() => { setPage(pageNum); scrollToTable(); }}
                    className={`micro-page-btn${page === pageNum ? " active" : ""}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              {Math.ceil(total / limit) > 5 && (
                <>
                  <span style={{
                    padding: "0 8px",
                    background: "#f5f5f5",
                    borderRadius: "4px",
                    color: "#222"
                  }}>...</span>
                  <button
                   onClick={() => { setPage(Math.ceil(total / limit)); scrollToTable(); }}
                    className={`micro-page-btn${page === Math.ceil(total / limit) ? " active" : ""}`}
                  >
                    {Math.ceil(total / limit)}
                  </button>
                </>
              )}
              <button
                disabled={page === Math.ceil(total / limit) || total === 0}
                onClick={() => { setPage(page + 1); scrollToTable(); }}
                className="micro-page-btn"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function OrgCard({ title, subtitle, active, onClick }) {
  return (
    <button
      className={`micro-card${active ? " active" : ""}`}
      onClick={onClick}
      style={{ cursor: 'pointer' }}
    >
      <div className="micro-card-title">{title}</div>
      <div className="micro-card-subtitle">{subtitle}</div>
      <span className="card-view-btn" tabIndex={-1} type="button">
        View
      </span>
    </button>
  );
}